/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import { Table } from '../../../../../../ui-kit';
import { useStockCardsQuery } from '../../../../../../generated/graphql';
import { useWarehousesScope } from '../../../../../warehouse/providers/WarehouseScopeProvider';
import { DeliveryListFormValues, PendingListItemFormValue, StockCard } from '../../types';
import ConnectCardsTableHeadCell from './ConnectCardsTableHeadCell';
import ConnectCardsTableRow from './ConnectCardsTableRow';
import AttachDialog from './AttachDialog';
import { connectCardsTableHeadCells } from './constants';

const ConnectCards = forwardRef<HTMLDivElement>((_props, ref) => {
    const { currentWarehouseId } = useWarehousesScope();
    const { values, setFieldValue } = useFormikContext<DeliveryListFormValues>();

    const [attachingItemIndex, setAttachingItemIndex] = useState<number | null>(null);

    const stockCardsIds = values.pendingListItems
        ? values.pendingListItems.map(({ deliveryOption }) => deliveryOption?.stockCardId).filter(id => !!id) as string[]
        : [];

    const { data: stockCards, loading } = useStockCardsQuery({
        variables: {
            stockCardsIds,
            warehouseIds: [currentWarehouseId as string],
        },
    });

    useEffect(() => {
        if (stockCards) {
            values.pendingListItems.forEach((item, index) => {
                if (item.stockCard) {
                    return;
                }

                const stockCard = stockCards.stockCards.find(({ id }) => id === item.deliveryOption?.stockCardId);

                if (stockCard) {
                    setItemStockCard(index, stockCard);
                    prefillConversionFields(index, stockCard);
                }
            });
        }
    }, [stockCards]);

    const setItemStockCard = (index: number, stockCard: StockCard) => {
        setFieldValue(`pendingListItems.${index}`, {
            ...values.pendingListItems[index],
            stockCard,
            vatRate: stockCard.tax.rate,
        });
    };

    const prefillConversionFields = (index: number, stockCard: Pick<StockCard, 'storageUnit' | 'id'>) => {
        const item = values.pendingListItems?.[index];
        if (item) {
            let finalAmount = item.quantity;
            let unitOfMeasure = stockCard.storageUnit;

            const { deliveryOption } = item;
            if (deliveryOption && deliveryOption.stockCardId === stockCard.id) {
                const { conversionMultiplier, conversionUnitType } = deliveryOption;
                finalAmount = item.quantity * conversionMultiplier;
                unitOfMeasure = conversionUnitType;
            }

            setFieldValue(`pendingListItems.${index}.finalAmount`, finalAmount);
            setFieldValue(`pendingListItems.${index}.unitOfMeasure`, unitOfMeasure);
        }
    };

    const handleItemEdit = (index: number) => (newItem: PendingListItemFormValue) => {
        setFieldValue(`pendingListItems.${index}`, newItem);
    };

    const handleItemDelete = (index: number) => () => {
        setFieldValue('pendingListItems', values.pendingListItems.filter((_, i) => i !== index));
    };

    const handleItemAttachStart = (index: number) => () => {
        setAttachingItemIndex(index);
    };

    const handleItemAttach = (card: StockCard) => {
        setItemStockCard(attachingItemIndex as number, card);
        prefillConversionFields(attachingItemIndex as number, card);

        setAttachingItemIndex(null);
    };

    const pendingListItems = values?.pendingListItems || [];

    return (
        <div ref={ref}>
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12}>
                    {loading ? (
                        <Box width='100%' display='flex' justifyContent='center'>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Table
                            isFirstColumnSticky
                            headCells={connectCardsTableHeadCells}
                            TableHeadCellComponent={ConnectCardsTableHeadCell}
                        >
                            {pendingListItems.map((item, index) => (
                                <ConnectCardsTableRow
                                    key={item.id}
                                    item={item}
                                    onEdit={handleItemEdit(index)}
                                    onAttach={handleItemAttachStart(index)}
                                    onDelete={handleItemDelete(index)}
                                />
                            ))}
                        </Table>
                    )}
                </Grid>
            </Grid>
            <AttachDialog
                isOpened={!isNil(attachingItemIndex)}
                item={isNil(attachingItemIndex) ? null : pendingListItems[attachingItemIndex]}
                onClose={() => setAttachingItemIndex(null)}
                onAttach={handleItemAttach}
            />
        </div>
    );
});

export default ConnectCards;
